import { type ReactNode } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2 } from "lucide-react";
import {
  useExcluirProjeto,
  usePreviewExclusaoProjeto,
  type PreviewExclusaoProjeto,
} from "@/hooks/useProjetos";
import { formatarMoedaBr } from "@/lib/sinistroValores";
import { toast } from "sonner";

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (error && typeof error === "object" && "message" in error) {
    return String((error as { message: unknown }).message);
  }
  return "Erro ao carregar preview da exclusão";
}

function fmtBRL(valor?: number | null): string {
  if (valor == null || !Number.isFinite(Number(valor))) return "—";
  return `R$ ${formatarMoedaBr(Number(valor))}`;
}

function fmtData(data?: string | null): string {
  if (!data) return "";
  const [ano, mes, dia] = data.split("T")[0].split("-");
  if (!ano || !mes || !dia) return data;
  return `${dia}/${mes}/${ano}`;
}

function PreviewSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <p className="font-medium text-foreground text-xs uppercase tracking-wide">
        {title}
      </p>
      <ul className="list-none space-y-1 pl-0">{children}</ul>
    </div>
  );
}

function PreviewLine({ children }: { children: ReactNode }) {
  return (
    <li className="rounded-md bg-muted/50 px-2 py-1.5 font-mono text-xs sm:text-sm">
      {children}
    </li>
  );
}

function buildPreviewSections(preview: PreviewExclusaoProjeto) {
  const sections: ReactNode[] = [];
  const { counts, items, projeto_id, cliente_nome, projeto } = preview;

  const clienteLabel = cliente_nome?.trim() || "Cliente sem nome";
  const projetoParts: string[] = [`#${projeto_id}`, clienteLabel];
  if (projeto?.valor_projeto != null) {
    projetoParts.push(`valor ${fmtBRL(projeto.valor_projeto)}`);
  }
  if (projeto?.valor_seguro != null) {
    projetoParts.push(`seguro ${fmtBRL(projeto.valor_seguro)}`);
  } else if (projeto?.orcamento_valor_total != null) {
    projetoParts.push(`orçamento ${fmtBRL(projeto.orcamento_valor_total)}`);
  }
  if (projeto?.status) projetoParts.push(projeto.status);

  sections.push(
    <PreviewSection key="projeto" title="Projeto">
      <PreviewLine>{projetoParts.join(" · ")}</PreviewLine>
    </PreviewSection>
  );

  if (!items) {
    const countLines: Array<[number, string]> = [
      [counts.orcamentos, "orçamento(s)"],
      [counts.pedidos, "pedido(s)"],
      [counts.contratos, "contrato(s)"],
      [counts.parcelas, "parcela(s)"],
      [counts.transacoes_financeiras, "transação(ões) financeira(s)"],
      [counts.agenda, "item(ns) de agenda"],
      [counts.sinistros, "sinistro(s)"],
      [counts.sinistro_comentarios, "comentário(s) de sinistro"],
      [counts.projeto_equipamentos, "equipamento(s) do projeto"],
      [counts.orcamento_equipamentos, "equipamento(s) de orçamento"],
      [counts.projeto_historico, "registro(s) de histórico"],
    ];
    const resumo = countLines.filter(([n]) => n > 0).map(([n, label]) => `${n} ${label}`);
    if (resumo.length > 0) {
      sections.push(
        <PreviewSection key="resumo" title="Resumo">
          {resumo.map((line) => (
            <PreviewLine key={line}>{line}</PreviewLine>
          ))}
        </PreviewSection>
      );
    }
    return sections;
  }

  if (items.orcamentos?.length) {
    sections.push(
      <PreviewSection key="orcamentos" title={`Orçamentos (${counts.orcamentos})`}>
        {items.orcamentos.map((o) => (
          <PreviewLine key={o.id}>
            #{o.id}
            {o.valor_total != null ? ` · total ${fmtBRL(o.valor_total)}` : ""}
            {o.valor_seguro != null ? ` · seguro ${fmtBRL(o.valor_seguro)}` : ""}
            {o.status ? ` · ${o.status}` : ""}
          </PreviewLine>
        ))}
      </PreviewSection>
    );
  }

  if (items.pedidos?.length) {
    sections.push(
      <PreviewSection key="pedidos" title={`Pedidos (${counts.pedidos})`}>
        {items.pedidos.map((p) => (
          <PreviewLine key={p.id}>
            #{p.id}
            {p.numero_pedido ? ` · ${p.numero_pedido}` : ""}
            {p.valor_total != null ? ` · ${fmtBRL(p.valor_total)}` : ""}
            {p.status ? ` · ${p.status}` : ""}
          </PreviewLine>
        ))}
      </PreviewSection>
    );
  }

  if (items.contratos?.length) {
    sections.push(
      <PreviewSection key="contratos" title={`Contratos (${counts.contratos})`}>
        {items.contratos.map((c) => (
          <PreviewLine key={c.id}>
            #{c.id}
            {c.numero_contrato ? ` · ${c.numero_contrato}` : ""}
            {c.valor != null ? ` · ${fmtBRL(c.valor)}` : ""}
            {c.status ? ` · ${c.status}` : ""}
          </PreviewLine>
        ))}
      </PreviewSection>
    );
  }

  if (items.parcelas?.length) {
    sections.push(
      <PreviewSection key="parcelas" title={`Parcelas (${counts.parcelas})`}>
        {items.parcelas.map((pa) => (
          <PreviewLine key={pa.id}>
            #{pa.id}
            {pa.numero_parcela != null && pa.total_parcelas != null
              ? ` · ${pa.numero_parcela}/${pa.total_parcelas}`
              : ""}
            {pa.valor != null ? ` · ${fmtBRL(pa.valor)}` : ""}
            {pa.data_vencimento ? ` · venc. ${fmtData(pa.data_vencimento)}` : ""}
            {pa.status ? ` · ${pa.status}` : ""}
          </PreviewLine>
        ))}
      </PreviewSection>
    );
  }

  if (items.transacoes_financeiras?.length) {
    sections.push(
      <PreviewSection
        key="transacoes"
        title={`Transações financeiras (${counts.transacoes_financeiras})`}
      >
        {items.transacoes_financeiras.map((t) => (
          <PreviewLine key={t.id}>
            #{t.id}
            {t.tipo ? ` · ${t.tipo}` : ""}
            {t.valor != null ? ` · ${fmtBRL(t.valor)}` : ""}
            {t.data_vencimento ? ` · venc. ${fmtData(t.data_vencimento)}` : ""}
            {t.status ? ` · ${t.status}` : ""}
          </PreviewLine>
        ))}
      </PreviewSection>
    );
  }

  if (items.agenda?.length) {
    sections.push(
      <PreviewSection key="agenda" title={`Agenda (${counts.agenda})`}>
        {items.agenda.map((a) => (
          <PreviewLine key={a.id}>
            #{a.id}
            {a.titulo ? ` · ${a.titulo}` : ""}
            {a.tipo ? ` · ${a.tipo}` : ""}
            {a.data_inicio ? ` · ${fmtData(a.data_inicio)}` : ""}
            {a.status ? ` · ${a.status}` : ""}
          </PreviewLine>
        ))}
      </PreviewSection>
    );
  }

  if (items.sinistros?.length) {
    sections.push(
      <PreviewSection key="sinistros" title={`Sinistros (${counts.sinistros})`}>
        {items.sinistros.map((s) => (
          <PreviewLine key={s.id}>
            #{s.id}
            {s.numero_sinistro ? ` · ${s.numero_sinistro}` : ""}
            {s.tipo ? ` · ${s.tipo}` : ""}
            {s.valor_estimado != null ? ` · ${fmtBRL(s.valor_estimado)}` : ""}
            {s.status ? ` · ${s.status}` : ""}
          </PreviewLine>
        ))}
      </PreviewSection>
    );
  }

  if (counts.sinistro_comentarios > 0) {
    sections.push(
      <PreviewSection
        key="sinistro_comentarios"
        title={`Comentários de sinistro (${counts.sinistro_comentarios})`}
      >
        <PreviewLine>{counts.sinistro_comentarios} registro(s)</PreviewLine>
      </PreviewSection>
    );
  }

  if (items.projeto_equipamentos?.length) {
    sections.push(
      <PreviewSection
        key="projeto_equipamentos"
        title={`Equipamentos do projeto (${counts.projeto_equipamentos})`}
      >
        {items.projeto_equipamentos.map((pe) => (
          <PreviewLine key={pe.id}>
            #{pe.id}
            {pe.quantidade != null ? ` · qtd ${pe.quantidade}` : ""}
            {pe.preco_unitario != null ? ` · unit. ${fmtBRL(pe.preco_unitario)}` : ""}
            {pe.valor != null && Number(pe.valor) > 0 ? ` · total ${fmtBRL(pe.valor)}` : ""}
          </PreviewLine>
        ))}
      </PreviewSection>
    );
  }

  if (items.orcamento_equipamentos?.length) {
    sections.push(
      <PreviewSection
        key="orcamento_equipamentos"
        title={`Equipamentos de orçamento (${counts.orcamento_equipamentos})`}
      >
        {items.orcamento_equipamentos.map((oe) => (
          <PreviewLine key={oe.id}>
            #{oe.id}
            {oe.orcamento_id != null ? ` · orç. #${oe.orcamento_id}` : ""}
            {oe.quantidade != null ? ` · qtd ${oe.quantidade}` : ""}
            {oe.preco_unitario != null ? ` · unit. ${fmtBRL(oe.preco_unitario)}` : ""}
            {oe.valor != null && Number(oe.valor) > 0 ? ` · total ${fmtBRL(oe.valor)}` : ""}
          </PreviewLine>
        ))}
      </PreviewSection>
    );
  }

  if (counts.projeto_historico > 0) {
    sections.push(
      <PreviewSection
        key="projeto_historico"
        title={`Histórico do projeto (${counts.projeto_historico})`}
      >
        <PreviewLine>{counts.projeto_historico} registro(s)</PreviewLine>
      </PreviewSection>
    );
  }

  return sections;
}

interface ExcluirProjetoDialogProps {
  projetoId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function ExcluirProjetoDialog({
  projetoId,
  open,
  onOpenChange,
  onSuccess,
}: ExcluirProjetoDialogProps) {
  const { data: preview, isLoading, isError, error } = usePreviewExclusaoProjeto(
    open ? projetoId : null
  );
  const excluirProjeto = useExcluirProjeto();

  const handleExcluir = async () => {
    if (!projetoId) return;
    try {
      const result = await excluirProjeto.mutateAsync(projetoId);
      toast.success(
        `Projeto #${projetoId} excluído. Cadastro de ${preview?.cliente_nome?.trim() || `cliente #${result.cliente_id}`} mantido.`
      );
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Erro ao excluir projeto";
      toast.error(message);
    }
  };

  const previewSections = preview ? buildPreviewSections(preview) : [];

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-xl max-h-[85vh] flex flex-col">
        <AlertDialogHeader>
          <AlertDialogTitle>Confirmar exclusão do projeto</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>
                Esta ação é <strong>irreversível</strong>. O cadastro do cliente
                será mantido; todos os dados operacionais vinculados ao projeto
                (incluindo sinistros legados vinculados ao projeto) serão removidos
                permanentemente.
              </p>
              {isLoading && (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Carregando preview...
                </div>
              )}
              {isError && (
                <p className="text-red-600">{getErrorMessage(error)}</p>
              )}
              {previewSections.length > 0 && (
                <div className="space-y-3 max-h-[45vh] overflow-y-auto pr-1">
                  <p className="font-medium text-foreground">Serão removidos:</p>
                  {previewSections}
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={excluirProjeto.isPending}>
            Cancelar
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              void handleExcluir();
            }}
            disabled={isLoading || isError || excluirProjeto.isPending}
            className="bg-red-600 hover:bg-red-700"
          >
            {excluirProjeto.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Excluindo...
              </>
            ) : (
              "Excluir permanentemente"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
